import { useState } from "react";
import { Icon } from "@/components/Icon/Icon";
import { PilotBarrier } from "@/components/PilotLock";
import { logEvent } from "@/lib/eventLogger";
import type { SensoryDetailMap } from "@/services/mock";
import { RadarFill } from "./RadarFill";
import { SR3Plot } from "./SR3Plot";

// Three-state drill-down receptor radar.
//
// Stages:
//   rest     — full receptor radar, every vertex hoverable / clickable.
//   armed    — first click on a receptor: vertex fills accent-soft, directions
//              update to coach the second click.
//   drilled  — second click on the armed receptor: cross-fade into the
//              detail-axes radar; a 140px mini receptor strip slides in above.
//   picked   — clicking a detail vertex while drilled selects it (chip in
//              the header reflects the deepest selection).
//
// Notes:
// - VertexLabel and the SVG are sized by the parent column width via
//   RadarFill (ResizeObserver) so the radar stays crisp under sidebar
//   density / viewport changes.
// - Reset wipes everything; ← Receptors exits drill but keeps a selection
//   so the recommender keeps its semantic state.

type SensoryRadarSelection =
  | { kind: "receptor"; receptor: string; armed: boolean }
  | { kind: "detail"; receptor: string; detail: string }
  | null;

type SensoryRadarStackProps = {
  label: string;
  axes: string[];
  target: number[];
  current: number[];
  detailMap: SensoryDetailMap;
  onSelectionChange?: (sel: SensoryRadarSelection) => void;
  /** AF Review override colors for the Current series. Forwarded to SR3Plot. */
  currentColor?: string;
  currentColorInk?: string;
  /** When true the radar arena alone renders behind a PilotBarrier — the
   *  title + directions stay crisp so the surface still reads as the Smell
   *  analysis. Used for the Smell radar during the Pilot release. */
  pilotLocked?: boolean;
};

export function SensoryRadarStack({
  label,
  axes,
  target,
  current,
  detailMap,
  onSelectionChange,
  currentColor,
  currentColorInk,
  pilotLocked = false,
}: SensoryRadarStackProps) {
  const [activeReceptor, setActiveReceptor] = useState<number | null>(null);
  const [drilled, setDrilled] = useState(false);
  const [detailIdx, setDetailIdx] = useState<number | null>(null);
  const [recHover, setRecHover] = useState<number | null>(null);
  const [detHover, setDetHover] = useState<number | null>(null);
  const [miniHover, setMiniHover] = useState<number | null>(null);

  const activeName = activeReceptor != null ? axes[activeReceptor] ?? null : null;
  const detail = activeName != null ? detailMap[activeName] ?? null : null;
  const detailName =
    detail && detailIdx != null ? detail.axes[detailIdx] ?? null : null;

  const stage: "rest" | "armed" | "drilled" | "picked" =
    activeReceptor == null
      ? "rest"
      : !drilled
      ? "armed"
      : detailIdx == null
      ? "drilled"
      : "picked";

  const clickReceptor = (i: number) => {
    logEvent("flavor_workbench_radar_vertex_clicked", {
      level: "receptor",
      vertex: axes[i] ?? "",
      receptor: null,
      label,
    });
    if (drilled) {
      // While drilled, clicking the mini strip just switches the active
      // receptor — no re-arm/re-drill cycle.
      if (i === activeReceptor) return;
      setActiveReceptor(i);
      setDetailIdx(null);
      emit({ kind: "receptor", receptor: axes[i], armed: false });
      return;
    }
    if (activeReceptor === i) {
      setDrilled(true);
      setDetailIdx(null);
      emit({ kind: "receptor", receptor: axes[i], armed: false });
    } else {
      setActiveReceptor(i);
      setDrilled(false);
      setDetailIdx(null);
      emit({ kind: "receptor", receptor: axes[i], armed: true });
    }
  };
  const clickDetail = (j: number) => {
    if (!drilled || activeReceptor == null || !detail) return;
    if (detailIdx === j) return;
    logEvent("flavor_workbench_radar_vertex_clicked", {
      level: "detail",
      vertex: detail.axes[j] ?? "",
      receptor: axes[activeReceptor] ?? null,
      label,
    });
    setDetailIdx(j);
    emit({
      kind: "detail",
      receptor: axes[activeReceptor],
      detail: detail.axes[j],
    });
  };
  const backToReceptors = () => {
    setDrilled(false);
    setDetailIdx(null);
    if (activeReceptor != null) {
      emit({
        kind: "receptor",
        receptor: axes[activeReceptor],
        armed: false,
      });
    }
  };
  const reset = () => {
    setDrilled(false);
    setDetailIdx(null);
    setActiveReceptor(null);
    emit(null);
  };
  const peelChip = () => {
    if (detailIdx != null) {
      setDetailIdx(null);
      if (activeReceptor != null) {
        emit({
          kind: "receptor",
          receptor: axes[activeReceptor],
          armed: false,
        });
      }
      return;
    }
    if (activeReceptor != null) {
      setActiveReceptor(null);
      setDrilled(false);
      emit(null);
    }
  };

  function emit(sel: SensoryRadarSelection) {
    onSelectionChange?.(sel);
  }

  const chipLabel = detailName || activeName || null;
  const hasState = drilled || activeReceptor != null || detailIdx != null;

  return (
    <div className="sr3-stack vstack" style={{ gap: 10, alignItems: "stretch" }}>
      <div className="sr3-header">
        <div className="label sr3-title">{label}</div>
        <div className="hstack" style={{ alignItems: "center", gap: 6 }}>
          {chipLabel && (
            <div key={chipLabel} className="sr3-chip" title={chipLabel}>
              <span className="sr3-chip-dot" />
              <span className="sr3-chip-label">{chipLabel}</span>
              <button
                type="button"
                onClick={peelChip}
                title={detailName ? "Clear detail selection" : "Clear selection"}
                className="sr3-chip-x"
              >
                <Icon name="x" size={10} />
              </button>
            </div>
          )}
          {hasState && (
            <button
              type="button"
              onClick={reset}
              className="sr3-reset"
              title="Reset all"
            >
              <Icon name="x" size={10} />
              Reset
            </button>
          )}
        </div>
      </div>

      <SR3Directions stage={stage} activeName={activeName} detailName={detailName} />

      {drilled && (
        <div className="sr3-strip">
          <div className="sr3-strip-radar">
            <SR3Plot
              axes={axes}
              target={target}
              current={current}
              size={140}
              selectedIdx={activeReceptor}
              activeIdx={activeReceptor}
              onVertexClick={clickReceptor}
              hovered={miniHover}
              onHover={setMiniHover}
              showLabels={false}
              currentColor={currentColor}
              currentColorInk={currentColorInk}
            />
            {miniHover != null && (
              <div className="sr3-strip-tip">{axes[miniHover]}</div>
            )}
          </div>
          <div className="vstack" style={{ gap: 2, minWidth: 0, flex: 1 }}>
            <div className="sr3-strip-eyebrow">Active receptor</div>
            <div className="sr3-strip-name">{activeName}</div>
          </div>
        </div>
      )}

      <PilotBarrier compact off={!pilotLocked}>
        <RadarFill>
          {(arenaSize) => (
            <>
              <div
                aria-hidden={drilled}
                className="sr3-arena-layer"
                style={{
                  opacity: drilled ? 0 : 1,
                  transform: drilled ? "scale(0.92)" : "scale(1)",
                  pointerEvents: drilled ? "none" : "auto",
                }}
              >
                <SR3Plot
                  axes={axes}
                  target={target}
                  current={current}
                  size={arenaSize}
                  selectedIdx={null}
                  activeIdx={activeReceptor}
                  onVertexClick={clickReceptor}
                  hovered={recHover}
                  onHover={setRecHover}
                  currentColor={currentColor}
                  currentColorInk={currentColorInk}
                />
              </div>
              <div
                aria-hidden={!drilled}
                className="sr3-arena-layer sr3-arena-layer-detail"
                style={{
                  opacity: drilled ? 1 : 0,
                  transform: drilled ? "scale(1)" : "scale(0.86)",
                  pointerEvents: drilled ? "auto" : "none",
                }}
              >
                {detail && (
                  <SR3Plot
                    axes={detail.axes}
                    target={detail.target}
                    current={detail.current}
                    size={arenaSize}
                    selectedIdx={detailIdx}
                    onVertexClick={clickDetail}
                    hovered={detHover}
                    onHover={setDetHover}
                    currentColor={currentColor}
                    currentColorInk={currentColorInk}
                  />
                )}
              </div>
            </>
          )}
        </RadarFill>
      </PilotBarrier>

      <div className="sr3-footer">
        <div>
          {drilled && (
            <button
              type="button"
              onClick={backToReceptors}
              className="sr3-back"
              title="Back to receptor view"
            >
              ← Receptors
            </button>
          )}
        </div>
        <SR3Legend />
      </div>
    </div>
  );
}

function SR3Directions({
  stage,
  activeName,
  detailName,
}: {
  stage: "rest" | "armed" | "drilled" | "picked";
  activeName: string | null;
  detailName: string | null;
}) {
  let text: string | null = null;
  if (stage === "rest") {
    text = "Click any vertex to activate a receptor.";
  } else if (stage === "armed") {
    text = `Activated ${activeName} — click again to inspect its detail axes.`;
  } else if (stage === "drilled") {
    text = `Now in ${activeName} detail. Click a detail axis to feed the recommender.`;
  } else {
    text = `Locked ${activeName} / ${detailName} — feeds the ingredient recommender.`;
  }
  return (
    <div key={stage} className="sr3-directions">
      {text}
    </div>
  );
}

function SR3Legend() {
  return (
    <div className="hstack" style={{ gap: 10, alignItems: "center" }}>
      <span className="mono sr3-legend">
        <span className="sr3-legend-swatch sr3-legend-target" />
        Target
      </span>
      <span className="mono sr3-legend">
        <span className="sr3-legend-swatch sr3-legend-current" />
        Current
      </span>
    </div>
  );
}
